// M149 — Publieke homepage (voor Google Verification + marketing).
// Toont een korte pitch, hoe het werkt en een expliciet Google-API-blok.
// Ook Engelse variant via ?lang=en (Google-reviewers lezen Engels).
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Sparkles, LineChart, Video, ArrowRight } from "lucide-react";

const TXT = {
  nl: {
    eyebrow: "JUSTEASYSEO",
    title: "SEO zonder gedoe.",
    lead: "JustEasySEO is een AI-marketingassistent voor Nederlandse ondernemers. We laten je zien wat er beter kan op je site, in Google en op social — en jij bepaalt zelf wat je doet.",
    cta_primary: "Aanmelden",
    cta_secondary: "Al klant? Inloggen",
    feat_title: "Wat je krijgt",
    features: [
      { icon: Sparkles, title: "Adviezen op maat",
         body: "Elke week concrete voorstellen voor je pagina's, koppen en zoekwoorden. In gewoon Nederlands." },
      { icon: LineChart, title: "Effect meetbaar",
         body: "Na 4 weken zie je zwart op wit wat het advies opleverde in Google Search Console en Analytics." },
      { icon: Video, title: "Vlogs & berichten",
         body: "Kant-en-klare korte video-scripts en social posts. Kopieer en plaats zelf." },
    ],
    how_title: "Zo werkt het",
    steps: [
      { n: "1", title: "Koppel je site", body: "Google Search Console en Analytics. Alleen lezen, geen wijzigingen." },
      { n: "2", title: "Ontvang adviezen", body: "Wij analyseren en maken elke week een top-5 kansen." },
      { n: "3", title: "Pas zelf toe", body: "Jij houdt de knop. Wij meten wat werkt." },
    ],
    google_title: "Wat we doen met je Google-data",
    google_body: "JustEasySEO leest Google Search Console en Google Analytics om jouw groeikansen te ontdekken. We wijzigen nooit iets zonder jouw akkoord. We verkopen geen data, we tonen geen advertenties, we gebruiken de gegevens uitsluitend voor jouw adviezen in JustEasySEO. Details staan in ons privacy-beleid.",
    google_link: "Bekijk het privacy-beleid",
    footer_privacy: "Privacy-beleid",
    footer_terms: "Voorwaarden",
    footer_contact: "Contact",
    footer_line: "© 2026 JustEasySEO — SEO zonder gedoe.",
    lang_switch: "English",
  },
  en: {
    eyebrow: "JUSTEASYSEO",
    title: "SEO without the hassle.",
    lead: "JustEasySEO is an AI marketing assistant for Dutch small-business owners. We show you what to improve on your site, in Google and on social — and you decide what to do.",
    cta_primary: "Sign up",
    cta_secondary: "Already a customer? Log in",
    feat_title: "What you get",
    features: [
      { icon: Sparkles, title: "Tailored advice",
         body: "Every week, concrete proposals for your pages, headlines and keywords. In plain language." },
      { icon: LineChart, title: "Measurable impact",
         body: "After 4 weeks you see, black on white, what the advice delivered in Google Search Console and Analytics." },
      { icon: Video, title: "Videos & posts",
         body: "Ready-made short video scripts and social posts. Copy and post them yourself." },
    ],
    how_title: "How it works",
    steps: [
      { n: "1", title: "Connect your site", body: "Google Search Console and Analytics. Read-only, no changes." },
      { n: "2", title: "Receive advice", body: "We analyse and produce a weekly top-5 opportunities." },
      { n: "3", title: "Apply it yourself", body: "You stay in control. We measure what works." },
    ],
    google_title: "What we do with your Google data",
    google_body: "JustEasySEO reads Google Search Console and Google Analytics to identify your growth opportunities. We never make changes without your explicit approval. We do not sell data, we do not show ads, and we use the information exclusively for your advice within JustEasySEO. Details are in our privacy policy.",
    google_link: "View the privacy policy",
    footer_privacy: "Privacy policy",
    footer_terms: "Terms",
    footer_contact: "Contact",
    footer_line: "© 2026 JustEasySEO — SEO without the hassle.",
    lang_switch: "Nederlands",
  },
};

export default function PublicHomePage() {
  const { user } = useAuth() || {};
  const [sp] = useSearchParams();
  const lang = sp.get("lang") === "en" ? "en" : "nl";
  const t = TXT[lang];
  // Ingelogd → redirect naar app.
  if (user) return <Navigate to="/" replace />;

  const otherLang = lang === "en" ? "" : "?lang=en";
  const privacyHref = `/privacy-policy${lang === "en" ? "?lang=en" : ""}`;
  const termsHref = `/voorwaarden${lang === "en" ? "?lang=en" : ""}`;

  return (
    <div className="min-h-screen bg-bg-canvas text-text-primary">
      {/* Top-bar */}
      <header className="max-w-[1200px] mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
        <Link to="/home" aria-label="JustEasySEO home" className="text-xl font-bold tracking-tight text-accent-teal">
          Just<span className="text-text-primary">Easy</span>SEO
        </Link>
        <div className="flex items-center gap-3">
          <Link to={`/home${otherLang}`} className="text-sm text-text-muted hover:text-text-primary">
            {t.lang_switch}
          </Link>
          <Link to="/login"
                 className="text-sm rounded-[10px] px-4 py-2 bg-accent-teal text-white hover:bg-accent-teal-dim font-medium"
                 style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}>
            {t.cta_primary}
          </Link>
        </div>
      </header>

      <main id="main-content" className="max-w-[1200px] mx-auto px-6 sm:px-12">
        {/* Hero */}
        <section className="pt-8 pb-16">
          <div className="text-xs font-semibold text-accent-teal"
                 style={{ letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {t.eyebrow}
          </div>
          <h1 className="mt-3 font-bold text-text-primary tracking-tight text-[36px] md:text-[56px]"
              style={{ lineHeight: 1.05, letterSpacing: "-0.02em" }}>
            {t.title}
          </h1>
          <p className="mt-5 text-lg text-text-muted max-w-2xl leading-relaxed">{t.lead}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup"
                   className="inline-flex items-center gap-1.5 rounded-[10px] px-6 py-3 bg-accent-teal text-white text-base font-medium hover:bg-accent-teal-dim"
                   style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}>
              {t.cta_primary} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="inline-flex items-center rounded-[10px] px-6 py-3 border border-border-hairline text-text-primary hover:bg-bg-panel text-base">
              {t.cta_secondary}
            </Link>
          </div>
        </section>

        {/* Features */}
        <section className="py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-8">{t.feat_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="rounded-[16px] p-6 bg-bg-panel"
                      style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
                  <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent-teal" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary mb-2">{f.title}</h3>
                  <p className="text-sm text-text-muted leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* How it works */}
        <section className="py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-8">{t.how_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.steps.map((s, i) => (
              <div key={i} className="rounded-[16px] p-6 bg-accent-mint">
                <div className="text-3xl font-bold text-accent-teal-dim mb-2">{s.n}</div>
                <h3 className="text-lg font-bold text-text-primary mb-2">{s.title}</h3>
                <p className="text-sm text-text-primary/80 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Google API disclosure */}
        <section className="py-12">
          <div className="rounded-[16px] p-8 bg-bg-panel border border-border-hairline">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-accent-teal" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-3">{t.google_title}</h2>
                <p className="text-sm md:text-base text-text-primary leading-relaxed">{t.google_body}</p>
                <Link to={privacyHref} className="mt-3 inline-flex items-center gap-1 text-sm text-accent-teal-dim hover:text-accent-teal font-medium">
                  {t.google_link} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border-hairline mt-16">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-12 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-sm text-text-muted">{t.footer_line}</div>
          <div className="flex items-center gap-6 text-sm">
            <Link to={privacyHref} className="text-text-muted hover:text-text-primary">{t.footer_privacy}</Link>
            <Link to={termsHref} className="text-text-muted hover:text-text-primary">{t.footer_terms}</Link>
            <a href="mailto:info@justeasyseo.com" className="text-text-muted hover:text-text-primary">{t.footer_contact}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
